/**
 * THE TREND CHART'S PADDER — it pads, and it must never subtract.
 *
 * ⚠️ IT LIVES HERE, NOT IN `TrendChart.tsx`, FOR ONE REASON: nothing runs the SPA's test
 * suite in this repository. `src/dashboard/frontend/app/` has no `node_modules`,
 * `@vitejs/plugin-react` is absent, and the root vitest config includes only `tests/**`
 * and `scripts/evals/**` — dozens of test files sit beside the components and are executed
 * by nothing (62 on 2026-09-14; this line said «~53» the day before, counted once and never
 * again), `TrendChart.test.tsx` among them, which is how a padder that deleted 100% of
 * the data under a shipped filter kept two green cases about weekend skipping. This module
 * imports no React, so `tests/dashboard/trend-chart-day-filter.test.ts` can drive it with
 * the runner every agent actually invokes.
 *
 * WHAT WAS WRONG, measured on the shipped bytes of `TrendChart.tsx` on 2026-09-14: the
 * padder took a BOOLEAN, `includeWeekends`, which `filters-schema.ts` derives as
 * `dayFilter === 'all'`. A boolean cannot hold three modes — 'weekdays' and 'weekends' both
 * arrive as `false` — so under "Weekends only" at daily granularity it skipped Saturday and
 * Sunday, i.e. every bucket the server had just returned, and did so BEFORE consulting
 * `present.get(t)`, so the deletion was unconditional. Two Saturdays carrying 21 requests
 * went in; five synthetic Mon–Fri zeros and 0 requests came out. The totals badge directly
 * above the same chart sums the UNPADDED series, so the card printed 21 and drew nothing.
 *
 * IT NOW ASKS THE SERVER'S OWN PREDICATE. `theDayFilterAdmits` is the single decider in
 * `src/services/analytics/day-filter-vocabulary.ts`, which is also what the WHERE clause
 * (`weekendExclusionSQL`) is checked against and what the single-day KPI's
 * `theDayTheHeadlineIsAbout` reads. Postgres `extract(dow from …)` and JavaScript
 * `getUTCDay()` number the week identically, which is what lets one function answer for
 * both sides. That module has NO imports, which is what makes it reachable from a browser
 * bundle at all — `filters.ts` pulls drizzle and the schema and cannot be. That emptiness
 * is now a case rather than a sentence: `trend-chart-day-filter.test.ts` walks the import
 * closure of every server module this app reaches and reds on the first bare specifier,
 * naming the path that got there. Before it existed, putting the schema into the vocabulary
 * was `tsc` 0 and green.
 */

import {
  theDayFilterAdmits,
  type DayFilterMode,
} from '../../../../../services/analytics/day-filter-vocabulary.js';

export type TrendGranularity = 'hourly' | 'daily' | 'weekly';

export interface TrendPoint {
  /** ISO-ish bucket string from the backend (e.g. "2026-04-14" or "2026-04-14T03:00:00Z"). */
  bucket: string;
  requests: number;
  activeUsers: number;
  /**
   * RM/Non-RM split of `requests`. When both fields are present (server opts in),
   * TrendChart renders a stacked bar: "RM" (primary fill) + "Non-RM" (muted fill).
   * Legacy callers that don't carry the split still render the classic single-bar variant.
   */
  rmRequests?: number;
  nonRmRequests?: number;
  /**
   * Optional rated subset of `requests` — surfaced only as a header-level badge on
   * Overview ("Total requests: X · Rated Y (N%)"). NOT used as a stack dimension.
   */
  rated?: number;
}

/**
 * Generate zero-filled bins between the first and last data point so the chart reads
 * continuously. No-op when the input has <2 points — filling synthesizes nothing useful and
 * risks anchoring the range in the wrong direction when the "real" span is one bucket.
 *
 * At daily granularity the padder skips the days the day filter does NOT admit, so the
 * chart never synthesizes a bar on a day the backend deliberately left out of the series.
 * It asks {@link theDayFilterAdmits}; it does not decide. Hourly and weekly granularities
 * are unaffected — hourly stays inside a single day, and a weekly bucket already folds
 * Sat/Sun into its Monday row, so dropping it would drop the week.
 *
 * ⚠️ THE SKIP IS A PADDING RULE, NOT A FILTER. A bucket the server returned is emitted
 * whatever day it falls on; only SYNTHETIC bins consult the mode. That ordering is the
 * whole defect: the shipped version tested the day first and never reached `present.get(t)`.
 *
 * WHICH CASE PINS WHICH HALF, because this sentence used to name the wrong one. `no mode in
 * the registry loses a bucket, or a request, to the padding` pins the MODE half: its series
 * comes from `theDayFilterAdmits`, so every bucket in it sits on a day the mode admits,
 * `padsOn` is true throughout and the order cannot show — putting the shipped order back
 * left it 5/5 GREEN, measured twice, before and after this sentence was written. The ORDER
 * half is pinned by `and it keeps a bucket the mode does NOT admit — the ORDER, not the
 * vocabulary`, whose series is the whole window, every day of the week, built by nothing
 * that knows what a mode admits.
 */
export function fillBins(
  data: TrendPoint[],
  granularity: TrendGranularity,
  mode: DayFilterMode = 'all',
): TrendPoint[] {
  if (data.length < 2) return data;

  const stepMs = granularity === 'hourly'
    ? 60 * 60 * 1000
    : granularity === 'weekly'
      ? 7 * 24 * 60 * 60 * 1000
      : 24 * 60 * 60 * 1000;

  // Index incoming points by their normalized bucket timestamp so we can cheaply look up
  // whether a generated bin already has real data.
  const present = new Map<number, TrendPoint>();
  for (const p of data) {
    const t = new Date(p.bucket).getTime();
    if (!Number.isNaN(t)) present.set(t, p);
  }
  const timestamps = [...present.keys()].sort((a, b) => a - b);
  const start = timestamps[0]!;
  const end = timestamps[timestamps.length - 1]!;

  // Only the daily granularity has a day-of-week to consult — see the doc block. Asking
  // once, here, also means an unknown mode throws before any bin is emitted rather than
  // silently producing a Mon–Fri chart.
  const padsOn = granularity === 'daily'
    ? (t: number): boolean => theDayFilterAdmits(mode, new Date(t).getUTCDay())
    : (): boolean => true;
  // Asked once up front, and the answer is deliberately discarded: the point is the THROW.
  // A range whose every bucket is real never reaches the padding branch, so without this an
  // unknown mode would render a chart instead of being refused. This file IS type-checked
  // (`tsc -b` inside `npm run build:frontend`) but only in the master-only `build` job, so
  // on a merge request the throw is the only thing standing here.
  void padsOn(start);

  const out: TrendPoint[] = [];
  for (let t = start; t <= end; t += stepMs) {
    const existing = present.get(t);
    if (existing) {
      // A bucket the server returned. It is data, not padding — emit it whatever day it
      // falls on. THIS IS THE ORDER THAT WAS WRONG.
      out.push(existing);
      continue;
    }
    if (!padsOn(t)) continue;
    // Zero-fill bar: if any real bucket carried the RM/Non-RM split, pad zero-buckets with
    // both roles = 0 too so the stacked series stay aligned across the whole range.
    // Undefined on the synthesised point would hide the padding bar on the stacked render.
    const anyRoleSplit = data.some(
      (d) => typeof d.rmRequests === 'number' || typeof d.nonRmRequests === 'number',
    );
    out.push({
      bucket: new Date(t).toISOString(),
      requests: 0,
      activeUsers: 0,
      ...(anyRoleSplit ? { rmRequests: 0, nonRmRequests: 0 } : {}),
    });
  }
  return out;
}
